import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { AccessLevel, UserFormData } from "../types/user"
import { Button } from "../../../components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "../../../components/ui/form"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select"
import { useToast } from "../../../hooks/use-toast"
import { Separator } from "../../../components/ui/separator"


const createUserSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  role: z.string(),
  permissions: z.object({
    patients: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    visits: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    inventory: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    outreach: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    admin: z.boolean().default(false),
  })
})

const editUserSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters").optional(),
  role: z.string(),
  permissions: z.object({
    patients: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    visits: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    inventory: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    outreach: z.enum([AccessLevel.NONE, AccessLevel.VIEW, AccessLevel.EDIT]),
    admin: z.boolean().default(false),
  })
})

type CreateUserFormValues = z.infer<typeof createUserSchema>
type EditUserFormValues = z.infer<typeof editUserSchema>
type UserFormValues = CreateUserFormValues & Partial<EditUserFormValues>


interface UserFormProps {
  mode?: 'create' | 'edit'
  defaultValues?: Partial<UserFormData>
  onSuccess?: () => void
}

export function UserForm({ mode = 'create', defaultValues = {}, onSuccess }: UserFormProps) {
  const schema = mode === 'create' ? createUserSchema : editUserSchema
  const form = useForm<UserFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...defaultValues,
      permissions: {
        patients: AccessLevel.NONE,
        visits: AccessLevel.NONE,
        inventory: AccessLevel.NONE,
        outreach: AccessLevel.NONE,
        admin: false,
        ...defaultValues.permissions,
      }
    },
  })
  const { toast } = useToast()

  async function onSubmit(_data: UserFormValues) {
    try {
      // TODO: Handle user creation/update
      toast({
        title: mode === 'create' ? "User created" : "User updated",
        description: mode === 'create' 
          ? "New user has been added successfully."
          : "User settings have been updated successfully.",
      })
      form.reset()
      onSuccess?.()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save user settings. Please try again.",
        variant: "destructive",
      })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid gap-8 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="user@example.com" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {mode === 'create' && (
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
        </div>

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role/Position</FormLabel>
              <FormControl>
                <Textarea placeholder="e.g. Head Nurse, Lab Technician, etc." {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="space-y-6">
          <div>
            <FormLabel className="text-lg">Permissions</FormLabel>
            <p className="text-sm text-muted-foreground mt-1">
              Configure access levels for different areas of the system
            </p>
          </div>
          
          <Separator />

          <div className="space-y-8">
            {/* Patient Records Permissions */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Patient Records</h4>
              <FormField
                control={form.control}
                name="permissions.patients"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={AccessLevel.NONE}>No Access</SelectItem>
                        <SelectItem value={AccessLevel.VIEW}>View Only</SelectItem>
                        <SelectItem value={AccessLevel.EDIT}>View & Edit</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Controls access to patient records and information
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            {/* Visits Permissions */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Visits</h4>
              <FormField
                control={form.control}
                name="permissions.visits"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={AccessLevel.NONE}>No Access</SelectItem>
                        <SelectItem value={AccessLevel.VIEW}>View Only</SelectItem>
                        <SelectItem value={AccessLevel.EDIT}>View & Edit</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Controls access to visit records and scheduling
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            {/* Inventory Permissions */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Inventory</h4>
              <FormField
                control={form.control}
                name="permissions.inventory"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={AccessLevel.NONE}>No Access</SelectItem>
                        <SelectItem value={AccessLevel.VIEW}>View Only</SelectItem>
                        <SelectItem value={AccessLevel.EDIT}>View & Edit</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Controls access to inventory management
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            {/* Outreach Permissions */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Outreach Programs</h4>
              <FormField
                control={form.control}
                name="permissions.outreach"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={AccessLevel.NONE}>No Access</SelectItem>
                        <SelectItem value={AccessLevel.VIEW}>View Only</SelectItem>
                        <SelectItem value={AccessLevel.EDIT}>View & Edit</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Controls access to outreach programs and activities
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            {/* Administrative Permissions */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Administrative Access</h4>
              <FormField
                control={form.control}
                name="permissions.admin"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Select onValueChange={(value) => field.onChange(value === "true")} defaultValue={field.value ? "true" : "false"}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="false">No Access</SelectItem>
                          <SelectItem value="true">Full Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Controls access to system settings and user management
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <Button type="submit" className="w-full sm:w-auto">
          {mode === 'create' ? 'Add User' : 'Save Changes'}
        </Button>
      </form>
    </Form>
  )
}
